import type * as models from 'pkg/api/models';
import type { Dateable } from 'pkg/api/models/dateable';
import type {
	EventFlags,
	EventTypes,
	RSVPInterval,
} from 'pkg/api/models/event';
import type { Record } from 'pkg/api/models/record';

export interface Booking extends Record, Dateable {
	scheduleId: number;
	scheduleItemId: number;
	eventId: number;
	userId: number;
	resourceId: number;
	forGroupId: number;
	byGroupId: number;
	title: string;
	description: string;
	timezone: string;
	endsAt: number;
	startsAt: number;
	startsAtLocal?: string;
	endsAtLocal?: string;

	inviteIntervalCount: number;
	inviteInterval: RSVPInterval;
	inviteBefore: number;
	reminderIntervalCount: number;
	reminderInterval: RSVPInterval;

	meetBeforeMinutes?: number;
	capacity?: number;
	hideParticipants?: boolean;
	isPrivate?: boolean;
	isPublic?: boolean;

	opponentGroupId?: number;
	opponentId?: number;
	isHome?: boolean;
	opponentGroup?: models.group.Group;
	opponent?: models.opponent.Opponent;

	type: EventTypes;
	source: BookingSource;
	scheduleItem: models.scheduleItem.ScheduleItem;
	resource: models.resource.Resource;
	resources: BookingResource[];
	schedule: models.schedule.Schedule;
	event?: models.event.Event;
	eventChanges?: BookingEventChange[];
	eventMatch?: BookingEventMatch;
}

export interface BookingEventMatch {
	isHome: boolean;
	opponentGroup?: models.group.Group;
	opponent?: models.opponent.Opponent;
}

export enum BookingSource {
	Scheduling = 'scheduling',
	Import = 'import',
	Tournament = 'tournament',
	Calendar = 'calendar',
}

export enum BookingEventChange {
	Deleted = 'deleted',
	Title = 'title',
	Description = 'description',
	Timezone = 'timezone',
	Time = 'time',
	Location = 'location',
	Opponent = 'opponent',
	Side = 'side',
}

export interface BookingResource extends Record {
	resourceId: number;

	resource: models.resource.Resource;
}

export interface BookingCreatePayload {
	eventId?: number;
	userId: number;
	resourceId?: number;
	resourceIds?: number[];
	forGroupId: number;
	byGroupId: number;
	title?: string;
	description?: string;
	timezone: string;
	endsAt: number;
	startsAt: number;
	/**
	 * startsAtLocal is the date time string for the event in the booking's timezone.
	 * this is basically a direct representation of the values in the form inputs, as opposed
	 * to the UNIX timestamp that startsAt is
	 */
	startsAtLocal?: string;
	/**
	 * endsAtLocal is the date time string for the event in the booking's timezone.
	 * this is basically a direct representation of the values in the form inputs, as opposed
	 * to the UNIX timestamp that endsAt is
	 */
	endsAtLocal?: string;
	type: EventTypes;
	inviteUsersToEvent?: boolean;
	inviteAdminAndStaffToEvent?: boolean;
	inviteUsersAsPending?: boolean;
	eventFlags?: models.event.EventFlags[];
	inviteIntervalCount?: number;
	inviteInterval?: RSVPInterval;
	reminderIntervalCount?: number;
	reminderInterval?: RSVPInterval;
	meetBeforeMinutes?: number | null;
	capacity?: number | null;
	hideParticipants?: boolean;
	isPrivate?: boolean;
	isPublic?: boolean;
	opponentGroupId?: number;
	opponentId?: number;
	isHome?: boolean;
}

export interface BookingUpdatePayload {
	eventId?: number;
	resourceId: number;
	forGroupId: number;
	title?: string;
	description?: string;
	timezone: string;
	endsAt: number;
	startsAt: number;
	/**
	 * startsAtLocal is the date time string for the event in the booking's timezone.
	 * this is basically a direct representation of the values in the form inputs, as opposed
	 * to the UNIX timestamp that startsAt is
	 */
	startsAtLocal?: string;
	/**
	 * endsAtLocal is the date time string for the event in the booking's timezone.
	 * this is basically a direct representation of the values in the form inputs, as opposed
	 * to the UNIX timestamp that endsAt is
	 */
	endsAtLocal?: string;
	resourceIds?: number[];
	eventFlags?: EventFlags[];
	inviteUsersAsPending?: boolean;
	inviteIntervalCount?: number;
	inviteInterval?: RSVPInterval;
	reminderIntervalCount?: number;
	reminderInterval?: RSVPInterval;
	meetBeforeMinutes?: number | null;
	capacity?: number | null;
	hideParticipants?: boolean;
	isPrivate?: boolean;
	isPublic?: boolean;
	opponentGroupId?: number | null;
	opponentId?: number | null;
	isHome?: boolean;
	overwriteEvent?: boolean;
}
